/* OverstaffedWidget — paarse melding voor overbezette groepen (sectie 9).
 * Nieuw: live SSE-updates (fallback naar poll) + 1-klik "Verschuif PM →"
 * coach die een dienst naar een naburige groep verplaatst.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { UsersRound, ChevronRight, ArrowRightLeft, Radio, Loader2 } from "lucide-react";
import { toast } from "sonner";
import useBkrLive from "@/hooks/useBkrLive";

export default function OverstaffedWidget() {
  const { snapshot, streamMode } = useBkrLive({ intervalSec: 20 });
  const items = useMemo(() => {
    const raw = snapshot?.items || [];
    return raw.filter((x) => x.status === "overstaffed");
  }, [snapshot]);

  const [coach, setCoach] = useState(null); // {sourceGroup, targets, shifts}
  const [busy, setBusy] = useState(false);
  const [pick, setPick] = useState({ shift_id: "", target_group_id: "", reason: "" });

  // Push-alarm: als er items zijn met status='overstaffed', trigger idempotente
  // notify-call zodat superadmin/locatiemanager een browser-push krijgen.
  // Backend zorgt zelf dat het per (org,group,datum) maar één keer stuurt.
  const notifiedKey = useRef("");
  useEffect(() => {
    const currentDate = snapshot?.datum || "";
    const gids = items.map((i) => i.group_id).sort().join(",");
    const key = `${currentDate}:${gids}`;
    if (!items.length || !currentDate || key === notifiedKey.current) return;
    notifiedKey.current = key;
    api.post("/bkr/notify-overstaffed").catch(() => { /* silent */ });
  }, [snapshot, items]);

  const openCoach = async (g) => {
    setBusy(true);
    try {
      // Andere groepen op zelfde locatie
      const groepsRes = await api.get(`/groups?location_id=${g.location_id}`);
      const gList = Array.isArray(groepsRes.data) ? groepsRes.data
        : groepsRes.data?.items || [];
      const targets = gList.filter((x) => x.id !== g.group_id);
      // Actieve shifts op de bron-groep vandaag (via /rooster/day, dan filteren)
      const today = snapshot?.datum || new Date().toISOString().slice(0, 10);
      const shRes = await api.get(`/rooster/day?datum=${today}`);
      const allShifts = Array.isArray(shRes.data) ? shRes.data
        : shRes.data?.items || shRes.data?.shifts || [];
      const active = allShifts.filter((s) =>
        s.group_id === g.group_id && !["geannuleerd", "ziek"].includes(s.status));
      setCoach({ source: g, targets, shifts: active });
      setPick({
        shift_id: active[0]?.id || "",
        target_group_id: targets[0]?.id || "",
        reason: `Overbezetting ${g.group_name || "groep"} — verschuiving via coach`,
      });
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Kan gegevens niet laden");
    }
    setBusy(false);
  };

  const submitReallocate = async () => {
    if (!pick.shift_id || !pick.target_group_id) {
      toast.error("Kies een shift en een doelgroep");
      return;
    }
    setBusy(true);
    try {
      await api.post("/bkr/reallocate-shift", pick);
      toast.success("Shift verschoven — dashboard ververst automatisch");
      setCoach(null);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Kon niet verschuiven");
    }
    setBusy(false);
  };

  if (items.length === 0) return null;

  return (
    <>
      <Card className="border-2 border-purple-300 bg-gradient-to-br from-purple-50 to-fuchsia-50 p-4 mb-4"
            data-testid="overstaffed-widget">
        <div className="flex items-start gap-3">
          <div className="rounded-full bg-purple-100 p-2.5 flex-shrink-0">
            <UsersRound className="h-5 w-5 text-purple-700" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-medium text-purple-900">
                {items.length === 1
                  ? "1 groep heeft meer PM aanwezig dan volgens BKR nodig is"
                  : `${items.length} groepen hebben meer PM aanwezig dan volgens BKR nodig is`}
              </span>
              <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-purple-100 text-purple-800 border border-purple-200">
                Overbezetting
              </span>
              <Badge variant="outline"
                     className="text-[10px] uppercase tracking-widest bg-white/50 border-purple-200 text-purple-700 gap-1"
                     data-testid="overstaffed-stream-mode">
                <Radio className={`h-3 w-3 ${streamMode === "sse" ? "text-emerald-600" : "text-amber-500"}`} />
                {streamMode === "sse" ? "Live" : streamMode === "poll" ? "Poll" : "…"}
              </Badge>
            </div>
            <p className="text-xs text-purple-700 mt-1">
              Geen wettelijke overtreding — mogelijk moment om flexibel te schuiven,
              een PM eerder naar huis te sturen of hulp aan een andere groep te bieden.
            </p>
            <ul className="mt-2 space-y-1">
              {items.slice(0, 6).map((g, i) => (
                <li key={i} className="text-sm flex items-center gap-2 text-purple-900"
                    data-testid={`overstaffed-item-${i}`}>
                  <ChevronRight className="h-3.5 w-3.5 text-purple-500 flex-shrink-0" />
                  <span className="font-medium truncate flex-1">{g.groep_name || g.group_name || g.name || "Groep"}</span>
                  <span className="text-purple-700 text-xs tabular-nums">
                    {g.medewerkers_aanwezig ?? g.aanwezig ?? g.pm_aanwezig ?? "?"}/{g.vereist ?? g.pm_vereist ?? "?"} PM
                  </span>
                  <Button size="sm" variant="ghost" disabled={busy}
                          onClick={() => openCoach(g)}
                          data-testid={`overstaffed-reallocate-${i}`}
                          className="h-7 px-2 text-purple-700 hover:bg-purple-100 hover:text-purple-900 gap-1">
                    <ArrowRightLeft className="h-3 w-3" />
                    Verschuif
                  </Button>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </Card>

      <Dialog open={!!coach} onOpenChange={(o) => { if (!o) setCoach(null); }}>
        <DialogContent className="bg-white" data-testid="overstaffed-coach-dialog">
          <DialogHeader>
            <DialogTitle>PM verschuiven — Overbezetting-coach</DialogTitle>
            <DialogDescription>
              Verplaats een actieve dienst van <strong>{coach?.source?.group_name || "de bron-groep"}</strong>
              {" "}naar een naburige groep op dezelfde locatie. Wettelijk BKR blijft van kracht.
            </DialogDescription>
          </DialogHeader>
          {coach && (
            <div className="space-y-3">
              <div>
                <Label>Kies actieve dienst *</Label>
                <select value={pick.shift_id}
                        onChange={(e) => setPick({ ...pick, shift_id: e.target.value })}
                        data-testid="coach-shift-select"
                        className="w-full border rounded px-2 py-2 text-sm bg-white">
                  {coach.shifts.length === 0 && <option value="">Geen actieve diensten gevonden</option>}
                  {coach.shifts.map((s) => (
                    <option key={s.id} value={s.id}>
                      {(s.medewerker_naam || s.medewerker_id || "PM")} · {s.start || "?"}–{s.eind || s.end || "?"}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <Label>Doelgroep *</Label>
                <select value={pick.target_group_id}
                        onChange={(e) => setPick({ ...pick, target_group_id: e.target.value })}
                        data-testid="coach-target-select"
                        className="w-full border rounded px-2 py-2 text-sm bg-white">
                  {coach.targets.length === 0 && <option value="">Geen andere groep op deze locatie</option>}
                  {coach.targets.map((g) => (
                    <option key={g.id} value={g.id}>{g.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <Label>Reden (voor audit)</Label>
                <Textarea rows={2}
                          value={pick.reason}
                          onChange={(e) => setPick({ ...pick, reason: e.target.value })}
                          data-testid="coach-reason" />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setCoach(null)} data-testid="coach-cancel">Annuleren</Button>
            <Button onClick={submitReallocate}
                    disabled={busy || !pick.shift_id || !pick.target_group_id}
                    data-testid="coach-submit"
                    style={{ background: "var(--sage)" }} className="text-white">
              {busy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <ArrowRightLeft className="h-4 w-4 mr-1" />}
              Verschuif definitief
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
